'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/axios';
import { API_URL } from '@/lib/config';

interface Slide {
  id: number;
  resim_yolu: string;
  baslik: string | null;
  aciklama: string | null;
  link: string | null;
  sira: number;
}

export default function Slider() {
  const [aktif, setAktif] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);

  useEffect(() => {
    api.get('/slider').then((res) => {
      setSlides(res.data || []);
      setYukleniyor(false);
    }).catch(() => setYukleniyor(false));
  }, []);

  useEffect(() => {
    if (slides.length === 0) return;
    const interval = setInterval(() => {
      setAktif((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const onceki = () => setAktif((prev) => (prev - 1 + slides.length) % slides.length);
  const sonraki = () => setAktif((prev) => (prev + 1) % slides.length);

  if (yukleniyor) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="bg-gray-200 animate-pulse rounded-[var(--radius-2xl)] h-64 md:h-96" />
      </div>
    );
  }

  if (slides.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <div className="relative overflow-hidden rounded-[var(--radius-2xl)] h-64 md:h-96 shadow-[var(--shadow-lg)]">
        {slides.map((slide, index) => {
          const icerik = (
            <img
              src={`${API_URL}${slide.resim_yolu}`}
              alt={slide.baslik || ''}
              className="w-full h-full object-cover"
            />
          );

          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === aktif ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {slide.link ? (
                <Link href={slide.link} className="block w-full h-full">
                  {icerik}
                  {(slide.baslik || slide.aciklama) && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                      {slide.baslik && <h3 className="text-white text-xl font-bold">{slide.baslik}</h3>}
                      {slide.aciklama && <p className="text-white text-sm opacity-80">{slide.aciklama}</p>}
                    </div>
                  )}
                </Link>
              ) : (
                <div className="w-full h-full">
                  {icerik}
                  {(slide.baslik || slide.aciklama) && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
                      {slide.baslik && <h3 className="text-white text-xl font-bold">{slide.baslik}</h3>}
                      {slide.aciklama && <p className="text-white text-sm opacity-80">{slide.aciklama}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {slides.length > 1 && (
          <>
            <button
              onClick={onceki}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition z-10"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={sonraki}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-black bg-opacity-40 hover:bg-opacity-60 text-white p-2 rounded-full transition z-10"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setAktif(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === aktif ? 'bg-white w-8' : 'bg-white bg-opacity-60 w-2'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}